import React, { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { useCurrentDomain } from "../context/DomainContext";
import { getCompaniesBySettingKey } from "../services/masterDataService";
import { THEME } from "../theme/themeColors";

const SettingUsageModal = ({ settingKeyId, settingName, onClose }) => {
  const { domain } = useCurrentDomain();
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const load = useCallback(async (page = 1, search = "") => {
    if (!domain || !settingKeyId) return;
    setLoading(true);
    try {
      const data = await getCompaniesBySettingKey(domain, settingKeyId, search, page, pageSize);
      setCompanies(data?.items || []);
      setTotalCount(data?.totalCount || 0);
      setPageNumber(page);
    } catch (err) {
      toast.error("Failed to load companies");
      setCompanies([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [domain, settingKeyId, pageSize]);

  useEffect(() => { load(1, ""); }, [load]);

  const handleSearch = () => load(1, searchTerm);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1100, padding: "16px"
    }} onClick={onClose}>
      <div style={{
        width: "min(900px, 96vw)", maxHeight: "85vh", background: "#fff",
        borderRadius: "10px", display: "flex", flexDirection: "column", overflow: "hidden"
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "12px 16px", background: THEME.primary, color: THEME.onPrimary, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: "15px" }}>{settingName} — Companies</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: THEME.onPrimary, cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "10px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", gap: "8px" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="Search company name or ID..."
            className="form-control form-control-sm"
            style={{ fontSize: "12px", maxWidth: "280px" }}
          />
          <button onClick={handleSearch} className="btn btn-sm" style={{ background: THEME.primary, color: THEME.onPrimary, border: "none", fontSize: "12px" }}>
            Search
          </button>
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border spinner-border-sm" style={{ color: THEME.primary }} /></div>
          ) : (
            <table className="table table-sm table-hover mb-0" style={{ fontSize: "13px" }}>
              <thead>
                <tr style={{ background: THEME.primary, color: THEME.onPrimary }}>
                  <th style={{ padding: "8px 12px" }}>Company Name</th>
                  <th style={{ padding: "8px 12px" }}>Company ID</th>
                </tr>
              </thead>
              <tbody>
                {companies.length === 0 ? (
                  <tr><td colSpan={3} className="text-center text-muted py-4">No companies found</td></tr>
                ) : companies.map(c => (
                  <tr key={c.companyId}>
                    <td style={{ padding: "7px 12px" }}>{c.companyName}</td>
                    <td style={{ padding: "7px 12px" }}>{c.companyId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalCount > 0 && (
          <div style={{ padding: "10px 16px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#6b7280" }}>
            <span>{(pageNumber - 1) * pageSize + 1}–{Math.min(pageNumber * pageSize, totalCount)} of {totalCount}</span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: "12px" }} disabled={pageNumber <= 1} onClick={() => load(pageNumber - 1, searchTerm)}>Prev</button>
              <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: "12px" }} disabled={pageNumber >= totalPages} onClick={() => load(pageNumber + 1, searchTerm)}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingUsageModal;